import { IconName } from '@lyse-as/formds-icons';
import { Component, Element, Event, EventEmitter, h, Host, Prop } from '@stencil/core';
import clsx from 'clsx';

import store from '@/store';

export type FdsButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'white-primary' | 'white-secondary';

export type FdsButtonLoaderPlacement = 'right' | 'left';

export type FdsButtonType = 'button' | 'submit';

export interface FdsButtonAriaProps {
  'aria-live': string;
  'aria-busy': string;
  'aria-label'?: string;
  'aria-disabled'?: string;
}

/**
 * @part fds-loader - The loading icon that shows when the button is in a loading state
 */
@Component({
  tag: 'fds-button',
  styleUrl: 'styles/button.scss',
  shadow: true,
})
export class Button {
  @Element() el!: HTMLFdsButtonElement;

  /**
   * The variant of button render
   */
  @Prop() variant: FdsButtonVariant = 'primary';

  /**
   * If true, the component will be disabled and users cannot interact with it
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * The type of button to display
   */
  @Prop() type: FdsButtonType = 'button';

  /**
   * The icon to display to the left of the button text
   */
  @Prop() iconLeft: IconName;

  /**
   * The icon to display to the right of the button text
   */
  @Prop() iconRight: IconName;

  /**
   * If true, the button will take up the full width of its container
   */
  @Prop() fullWidth: boolean;

  /**
   * If true, this property sets the tabIndex of the button to -1, meaning it can't be focused
   */
  @Prop() notFocusable = false;

  /**
   * If true, a loading icon will be displayed instead of the button text. Use this to indicate to the user that something is loading as a result of a click, and that we are awaiting a response
   */
  @Prop() isLoading = false;

  /**
   * If true, a CTA (call-to-action) variant of the button will be rendered
   */
  @Prop() isCta = false;

  /**
   * If set, this text will be displayed when the button is in a loading state, instead of the button text
   */
  @Prop() loadingText: string;

  /**
   * Wether to render the loading icon on the left- or right-hand side of the loading text
   */
  @Prop() loaderPlacement: FdsButtonLoaderPlacement = 'left';

  /**
   * The name of the form that this button is connected to, if any
   */
  @Prop() form?: string | HTMLFormElement;

  /**
   * A label to use for the button if you don't want to use the button text as the label. This is useful if you want to use an icon as the button text, but still want to provide a label for screen readers
   */
  // eslint-disable-next-line @stencil-community/reserved-member-names
  @Prop() ariaLabel: string;

  /**
   * Emitted when the button is focused
   */
  @Event() fdsFocus: EventEmitter<void>;

  /**
   * Emitted when the button is blurred
   */
  @Event() fdsBlur: EventEmitter<void>;

  constructor() {
    this.handleClick = this.handleClick.bind(this);
    this.handleBtnFocused = this.handleBtnFocused.bind(this);
    this.handleBtnBlurred = this.handleBtnBlurred.bind(this);
  }

  private handleBtnFocused(event: FocusEvent) {
    event.stopPropagation();
    this.fdsFocus.emit();
  }

  private handleBtnBlurred(event: FocusEvent) {
    event.stopPropagation();
    this.fdsBlur.emit();
  }

  private hasTextContent() {
    const text = this.el.textContent;
    return !!text;
  }

  private renderIcon(placement: FdsButtonLoaderPlacement) {
    const hasTextContent = this.hasTextContent();
    return (
      <span class={hasTextContent ? `button-icon-${placement}` : 'button-icon'}>
        <fds-icon name={placement === 'left' ? this.iconLeft : this.iconRight} />
      </span>
    );
  }

  private findForm(): HTMLFormElement | null {
    const { form } = this;
    if (form instanceof HTMLFormElement) {
      return form;
    }
    if (typeof form === 'string') {
      const el = document.querySelector(`#${form}`);
      if (el instanceof HTMLFormElement) {
        return el;
      }
    }

    return undefined;
  }

  private handleClick(ev: Event) {
    if (this.disabled || this.isLoading) {
      ev.stopPropagation();
      return;
    }

    const { el } = this;
    if (this.type === 'submit') {
      let formEl = this.findForm();
      const { form } = this;

      if (!formEl && form !== undefined) {
        if (typeof form === 'string') {
          console.error(
            `Form with selector: "#${form}" could not be found. Verify that the id is correct and the form is rendered in the DOM`,
          );
        } else {
          console.error(
            `The provided "form" element is invalid. Verify that the form is a HTMLFormElement and rendered in the DOM.`,
          );
        }

        return;
      }

      if (!formEl) {
        formEl = el.closest('form');
      }

      if (formEl) {
        ev.preventDefault();

        const fakeButton = document.createElement('button');
        fakeButton.type = this.type;
        fakeButton.style.display = 'none';
        formEl.append(fakeButton);
        fakeButton.click();
        fakeButton.remove();
      }
    }
  }

  private getAriaProps() {
    let ariaProps: FdsButtonAriaProps = {
      'aria-live': 'assertive',
      'aria-busy': this.isLoading ? 'true' : 'false',
      'aria-disabled': this.disabled ? 'true' : 'false',
    };

    if (!this.hasTextContent() && this.ariaLabel) {
      ariaProps = {
        ...ariaProps,
        'aria-label': this.ariaLabel,
      };
    }

    return ariaProps;
  }

  render() {
    const ariaProps = this.getAriaProps();

    const hostContainerClasses = clsx({
      'fds-button': true,
      [`fds-button--${this.variant}`]: true,
      [store.mode]: true,
      'fds-button--disabled': this.disabled,
      'fds-button--full-width': this.fullWidth,
    });

    const buttonContainerClasses = clsx({
      'fds-button__button': true,
      'fds-button__button--loading': this.isLoading,
      'fds-button__button--full-width': this.fullWidth,
      'fds-button__button--no-text': this.isLoading && !this.loadingText,
      [`fds-button__button--${this.loaderPlacement}`]: true,
      'fds-button__button--icon-button': !this.hasTextContent(),
      'fds-button__button--cta': this.isCta,
    });

    return (
      <Host class={hostContainerClasses}>
        <button
          class={buttonContainerClasses}
          disabled={this.disabled}
          type={this.type}
          tabIndex={this.notFocusable ? -1 : 0}
          onClick={this.handleClick}
          {...ariaProps}
          onFocus={this.handleBtnFocused}
          onBlur={this.handleBtnBlurred}
        >
          {this.isLoading && this.loaderPlacement === 'left' && (
            <fds-loader exportparts="fds-loader" color="currentColor" />
          )}
          {this.iconLeft && this.renderIcon('left')}
          {this.isLoading && this.loadingText ? this.loadingText : <slot />}
          {this.iconRight && this.renderIcon('right')}
          {this.isLoading && this.loaderPlacement === 'right' && (
            <fds-loader exportparts="fds-loader" color="currentColor" />
          )}
        </button>
      </Host>
    );
  }
}
